import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import ModuleToolTipContainer from 'components/global/ModuleToolTipContainer';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAppTracking from 'hooks/useAppTracking';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';
import useRestriction, { SYSTEM_LIMITS } from 'hooks/useRestriction';

import WhatsappBusinessModal from 'pages/OtherIntegrations/Integrations/Modals/WhatsappBusiness/WhatsappBusinessModal';
import BrandForm from 'pages/Settings/Brands/BrandForm';

import { AUTOCOMPLETE, ENUMS, WHATSAPP } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';
import PATHS from 'utils/paths';

import { faEye } from '@fortawesome/pro-solid-svg-icons';

const View = ({
	modalName,
	title,
	mode,
	smsTypes,
	isLoading,
	fetchAutocompleteData,
	templateData,
	onSubmit,
	handleCloseModal,
	formMethods,
	setIsMinimizing
}) => {
	const {
		setValue,
		handleSubmit,
		watch,
		formState: { isDirty }
	} = formMethods;

	const isRestricted = useRestriction();
	const isAuthorized = useAccessControl();
	const { track } = useAppTracking();
	const { t } = useTranslation();
	const navigate = useNavigate();

	// Constants
	const brandAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);
	const brandAutocompleteLabels = useMemo(() => ['Name'], []);
	const connectionAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);
	const connectionAutocompleteLabels = useMemo(() => ['Name'], []);

	const templateAutocompleteColumns = useMemo(() => [{ accessor: 'name', width: 200 }], []);
	const templateAutocompleteLabels = useMemo(() => ['name'], []);

	const defaultColSpan = {
		md: 6
	};

	// Whatsapp supported language codes : https://support.wati.io/l/en/article/8qf6ekwd88-what-are-the-supported-languages-for-template-update

	const fetchTemplateAutoCompleteData = useCallback(
		async (props) =>
			templateData.filter((item) =>
				props?.keyword
					? item.name.toLowerCase().includes(props?.keyword.toLowerCase())
					: props?.IDs
						? props?.IDs.includes(item.ID)
						: true
			),
		[templateData]
	);

	const connectionID = watch('connectionID');
	useEffect(() => {
		connectionID && fetchAutocompleteData(connectionID);
	}, [connectionID]);

	return (
		<Modal
			fullwidth
			maxWidth="medium"
			name={modalName}
			className="modal-whatsapp-settings"
			minimizeButton={(show, close) => (
				<MinimizeButton
					onShow={() => {
						show();
						setIsMinimizing(false);
					}}
					onClose={close}
					label={title}
					tooltip={['edit', 'view'].includes(mode) ? watch('name') : ''}
				/>
			)}
			onMinimize={() => {
				setIsMinimizing(true);
			}}>
			<ModalTitle>
				<Typography variant="p1">{title}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					{/* settingName */}
					<FormItem label="name" colSpan={defaultColSpan} isLoading={isLoading} required>
						<StringInput name="name" disabled={mode === 'view'} />
					</FormItem>
					{/* settingName */}
					{/* messageType */}
					<FormItem label="messageType" colSpan={defaultColSpan} isLoading={isLoading} required>
						<EnumSelect
							searchable={false}
							data={smsTypes}
							value={watch('messageType')}
							name="messageType"
							valueRemovable={false}
							setValue={(value) => {
								setValue(`messageType`, value, { shouldValidate: true, shouldDirty: true });
							}}
							disabled={mode === 'view'}
						/>
					</FormItem>
					{/* messageType */}
					{/* connectionID */}
					<FormItem label="connectionName" colSpan={defaultColSpan} isLoading={isLoading} required>
						<AutoComplete
							name="connectionID"
							autoCompleteName="whatsappBusinessConnections"
							columns={connectionAutocompleteColumns}
							selectionLabelAccessor={connectionAutocompleteLabels}
							value={connectionID}
							setValue={(value, firstRender) => {
								if (firstRender) {
									return;
								}
								setValue('connectionID', value?.id ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							valueRemovable={false}
							firstItem={{
								action: () =>
									NiceModal.show(WhatsappBusinessModal, {
										setConnection: (value) => {
											setValue(`connectionID`, value, {
												shouldValidate: true,
												shouldDirty: true
											});
										}
									}),
								label: <Typography color="var(--blue)">addConnection</Typography>
							}}
							disabled={mode === 'view'}
						/>
					</FormItem>
					{/* connectionID */}
					{/* templateName */}
					<FormItem label="templateName" colSpan={defaultColSpan} isLoading={isLoading} required>
						<AutoComplete
							name="templateOTOId"
							columns={templateAutocompleteColumns}
							selectionLabelAccessor={templateAutocompleteLabels}
							value={watch('templateOTOId')}
							disabled={mode === 'view' || !templateData?.length}
							disabledTooltip="tooltip.wpNoTemplateMessage"
							setValue={(value) => {
								setValue('templateOTOId', value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
								if (value) {
									setValue('language', value.language ?? null);
								} else {
									setValue('language', null);
								}
							}}
							valueRemovable={false}
							fetchFunction={fetchTemplateAutoCompleteData}
						/>
					</FormItem>
					{/* templateName */}
					{/* language */}
					<FormItem
						label="language"
						colSpan={defaultColSpan}
						isLoading={isLoading}
						toolTip={
							watch('language')
								? t('tooltip.wpSettingsLangMessage', {
										language: t(watch('language'))
									})
								: t('tooltip.wpSettingLanguage')
						}>
						<StringInput name="language" value={watch('language')} disabled />
					</FormItem>
					{/* language */}
					{/* brandID */}
					<FormItem label="brand" colSpan={defaultColSpan} isLoading={isLoading} roleName="BrandingModule">
						<AutoComplete
							name="brandID"
							autoCompleteName={AUTOCOMPLETE.BRANDS}
							columns={brandAutocompleteColumns}
							selectionLabelAccessor={brandAutocompleteLabels}
							value={watch('brandID')}
							setValue={(value) => {
								setValue('brandID', value?.ID ?? null, { shouldValidate: true, shouldDirty: true });
							}}
							firstItem={
								isAuthorized('/autocomplete/brand/addANewBrand')
									? {
											action: () =>
												NiceModal.show(BrandForm, {
													setBrand: (value) => {
														setValue(`brandID`, value, {
															shouldValidate: true,
															shouldDirty: true
														});
													}
												}),
											label: <Typography color="var(--blue)">addANewBrand</Typography>,
											disabled: (dataSize) => isRestricted(SYSTEM_LIMITS.BRAND, dataSize),
											disabledTooltip: (
												<div style={{ display: 'block' }}>
													<ModuleToolTipContainer
														infoImage="lottie_multiBrand"
														title="multiBrandModule"
														description="multiBrandModuleSubtitle"
													/>

													<Button
														style={{ marginLeft: 'Auto', marginRight: 'Auto' }}
														className="mt-3"
														label="showPlans"
														icon={faEye}
														variant="blue"
														onMouseDown={() => {
															track('moreThan3Brands');
															navigate(PATHS.OTO_MODULES);
														}}
													/>
												</div>
											)
										}
									: undefined
							}
							disabled={mode === 'view'}
						/>
					</FormItem>
					{/* brandID */}
					{/* status */}
					<FormItem label="status" colSpan={defaultColSpan} isLoading={isLoading} required>
						<EnumSelect
							searchable={false}
							enumName={ENUMS.ACTIVE_INACTIVE}
							name={`status`}
							valueRemovable={false}
							value={watch(`status`)}
							setValue={(value) => {
								setValue(`status`, value === 'deliveryOTPSms' ? 'active' : value, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							disabled={mode === 'view'}
						/>
					</FormItem>
					{/* status */}
				</Form>
			</ModalContent>
			{mode !== 'view' && (
				<ModalActions>
					<Button label="cancel" variant="outline" onClick={handleCloseModal} />
					<Button
						label="save"
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						onClick={handleSubmit(onSubmit)}
						iconPos="right"
					/>
				</ModalActions>
			)}
		</Modal>
	);
};

const WhatsappSettingForm = NiceModal.create(({ id, mode = 'add', modalName }) => {
	// States
	const [templateData, setTemplateData] = useState([]);
	const [isLoading, setIsLoading] = useState(false);

	// Hooks
	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();
	const modal = useModal();
	const { closeModal } = useModalRouter();
	const { showError, showSuccess } = useAlert();
	const [isMinimizing, setIsMinimizing] = useState(false);
	const isAuthorized = useAccessControl();
	const smsTypes = useMemo(() => {
		const types = [
			'trackingSms',
			'addressVerificationSms',
			'feedbackSms',
			'undeliveredAttemptSms',
			'returnRequestSMS'
		];

		if (isAuthorized('OTOFlexModule')) {
			types.push('deliveryOTPSms');
		}

		if (isAuthorized('ClickAndCollectModule')) {
			types.push('clickCollectSms');
		}

		return types;
	}, []);

	const title = useMemo(() => {
		const titles = {
			add: 'addWhatsappSetting',
			edit: 'editWhatsappSetting',
			view: 'viewWhatsappSetting'
		};

		return titles[mode];
	}, [mode]);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					ID: yup.number().nullable(),
					name: yup.string().nullable().required(),
					messageType: yup.string().nullable().required(),
					connectionID: yup.number().nullable().required(),
					brandID: yup.number().nullable(),
					language: yup.string().nullable(),
					templateOTOId: yup.number().nullable().required(),
					status: yup.string().nullable().required()
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			ID: null,
			name: null,
			messageType: null,
			connectionID: null,
			brandID: null,
			language: null,
			templateOTOId: null,
			status: null
		}
	});

	const {
		reset,
		setError,
		formState: { isDirty, isSubmitting, isSubmitSuccessful }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting && !isMinimizing);

	// Functions
	const handleCloseModal = () => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	};

	const onSubmit = async (data) => {
		const url = mode === 'add' ? WHATSAPP.ADD_WHATSAPP_SETTING : WHATSAPP.UPDATE_WHATSAPP_SETTING;

		await axiosPrivate
			.post(url, {
				...data
			})
			.then((response) => {
				if (response.status === 200) {
					showSuccess();
					dispatch(updateDynamicListData(DYNAMICLISTS.WHATSAPP_SETTINGS));
					handleCloseModal();
				}
			})
			.catch((error) => {
				showError();
				AppLogger.error(error);
			});
	};

	const fetchAutocompleteData = useCallback(async (connectionID) => {
		setIsLoading(true);
		await axiosPrivate
			.post(WHATSAPP.GET_TEMPLATES_AUTOCOMPLETE, {
				connectionID
			})
			.then((response) => {
				if (!response.data.length) {
					showError({
						message: 'tooltip.wpNoTemplateMessage'
					});

					setError('templateOTOId', { type: 'custom', message: 'tooltip.wpNoTemplateMessage' });
				}
				setTemplateData(response.data);
			})
			.catch((error) => {
				setTemplateData([]);
				showError();
				AppLogger.error(error);
			});
		setIsLoading(false);
	}, []);

	const getWhatsappSettingByID = useCallback(async (ID) => {
		setIsLoading(true);
		await axiosPrivate
			.post(WHATSAPP.GET_WHATSAPP_SETTING, { ID })
			.then((response) => {
				if (response.status === 200) {
					const { data } = response;
					reset({
						ID: data?.ID ?? null,
						name: data?.name ?? null,
						messageType: data?.otoMessageType ?? null,
						connectionID: data?.connectionID ?? null,
						brandID: data?.brandID ?? null,
						templateOTOId: data?.templateOTOId ?? null,
						status: data?.status ?? null
					});
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			});
		setIsLoading(false);
	}, []);

	// Effects
	useEffect(() => {
		if (id) {
			getWhatsappSettingByID(id);
		}
	}, []);

	return (
		<View
			key={templateData.length}
			{...{
				modalName,
				title,
				mode,
				smsTypes,
				isLoading,
				formMethods,
				fetchAutocompleteData,
				templateData,
				handleCloseModal,
				onSubmit,
				setIsMinimizing
			}}
			formMethods={formMethods}
		/>
	);
});

export default WhatsappSettingForm;
